import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccessRightsService } from '../../services/access-rights.service';
import { AccessRights } from '../../../shared/models/info/access-rights';
import { FeedbackButtonComponent } from '../common/feedback-button/feedback-button.component';
import { filter, Subscription, take } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { LogOutIconComponent } from '../icons/log-out-icon/log-out-icon.component';
import { ROUTE_PATHS } from '../../../shared/constants/route-paths';
import { prefixWithSlash } from '../../../shared/internal/prefix-with-slash';
import { IfPolicySatisfiedDirective } from '../../directives/if-policy-satisfied.directive';
import { Policy } from '../../../shared/models/info/policy';
import { ApiCallLogApiService } from '../../services/api/api-call-log-api.service';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes-tracker.service';
import { AppInsightsService } from '../../services/app-insights.service';
import { errorToString } from '../../common/common-utils';
import { NotificationDropdown } from '../common/notifications/notification-dropdown/notification-dropdown';
import { AgentActionDirective } from '../../directives/agent-action.directive';

@Component({
  selector: 'app-header',
  imports: [
    FeedbackButtonComponent,
    LogOutIconComponent,
    IfPolicySatisfiedDirective,
    RouterModule,
    NotificationDropdown,
    AgentActionDirective,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  public selectedSwPartNumberId?: string;
  public loggedInUserText?: string = '';
  public usersRoleText?: string = '';
  public displayMainMenu = false;
  public policy = Policy;
  public errorCodeRouteIsActive = false;

  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private router = inject(Router);
  private accessRightsService = inject(AccessRightsService);
  private appInsightsService = inject(AppInsightsService);
  private apiCallLogApi = inject(ApiCallLogApiService);
  private readonly unsavedChangesService = inject(UnsavedChangesTrackerService);
  private routerSubscription?: Subscription;
  private msalBroadcastServiceSubscription?: Subscription;
  private urlsWithoutMainMenu: string[] = [
    prefixWithSlash(ROUTE_PATHS.insufficientRights),
    prefixWithSlash(ROUTE_PATHS.signedOut),
    prefixWithSlash(ROUTE_PATHS.signIn),
    prefixWithSlash(ROUTE_PATHS.signInFailed),
  ];

  public ngOnInit(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.displayMainMenu = !this.urlsWithoutMainMenu.includes(this.router.url);
      });

    this.msalBroadcastServiceSubscription = this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1)
      )
      .subscribe(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
        }
      });
  }

  public ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    if (this.msalBroadcastServiceSubscription) {
      this.msalBroadcastServiceSubscription.unsubscribe();
    }
  }

  public get isLoggedIn(): boolean {
    const loggedIn = this.msalService.instance.getAllAccounts().length > 0;

    if (loggedIn) {
      this.refreshAccessRights();
    }

    return loggedIn;
  }

  public logout(): void {
    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(() => {
      this.performLogout();
    });

    if (!blocked) {
      this.performLogout();
    }
  }

  public login(): void {
    this.msalService.initialize().subscribe({
      next: () => {
        this.msalService.loginRedirect().subscribe({
          next: () => {
            this.refreshAccessRights();
            console.log('Login successful');
          },
          error: (error) => {
            console.error('Login failed', errorToString(error));
            this.router.navigate([prefixWithSlash(ROUTE_PATHS.signInFailed)]);
          },
        });
      },
    });
  }

  public downloadApiCallLogs(): void {
    this.accessRightsService.actOnAccessRights((ar) => {
      if (ar.authorized(Policy.canDownloadApiCallLogs)) {
        this.apiCallLogApi.downloadApiCallLogs().subscribe({
          next: (blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            a.download = `api-call-logs-${timestamp}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          },
          error: (error) => {
            console.error('Download failed', errorToString(error));
          },
        });
      }
    });
  }

  private refreshAccessRights(): void {
    this.accessRightsService.actOnAccessRights((ar) => {
      this.setLoggedInUserText(ar);
    });
  }

  private setLoggedInUserText(ar: AccessRights): void {
    this.loggedInUserText = `${ar.userName}`;
    this.usersRoleText = `${ar.roles.join(', ')}`;
  }

  private performLogout(): void {
    this.msalService.instance.setActiveAccount(null);
    this.msalService.logoutRedirect({ postLogoutRedirectUri: prefixWithSlash(ROUTE_PATHS.signedOut) }).subscribe({
      next: () => {
        console.log('Logout successful');

        this.accessRightsService.clearAccessRights();
        this.appInsightsService.clearUserContext();
        this.loggedInUserText = '';
      },
      error: (error) => {
        console.error('Logout failed', errorToString(error));
      },
    });
  }
}
